import {gql} from '@apollo/client';

export const GET_USER_PLANS = gql`
  query GetUserPlans($status: PlanStatus, $limit: Int, $offset: Int) {
    planapusPlans(status: $status, limit: $limit, offset: $offset) {
      id
      userId
      goal
      summary
      status
      createdAt
      updatedAt
      steps {
        id
        title
        description
        estimatedDurationDays
        kind
        status
        parentStepId
        dependsOn
      }
      phases {
        id
        title
        description
        order
        estimatedDurationDays
      }
    }
  }
`;

/**
 * Get all plans accessible to the current user with per-plan access metadata.
 *
 * Returns a unified list of:
 * - Plans where user is the owner (accessLevel=ADMIN, isOwner=true)
 * - Plans where user is a participant (accessLevel=ADMIN or ROLE_MEMBER)
 *
 * Each plan includes the viewer's specific access level for that plan.
 * Use this for the plan list page to show accurate access labels.
 *
 * Authorization: Access is per-(planId, personId) scoped.
 * A person who is ADMIN in Plan A may be ROLE_MEMBER in Plan B.
 */
export const GET_MY_ACCESSIBLE_PLANS = gql`
  query GetMyAccessiblePlans($status: PlanStatus, $limit: Int, $offset: Int) {
    myAccessiblePlans(status: $status, limit: $limit, offset: $offset) {
      id
      goal
      summary
      status
      createdAt
      updatedAt
      accessLevel
      isOwner
      accessLabel
      visibleStepCount
      totalStepCount
      progressPercent
    }
  }
`;

export const GET_PLAN = gql`
  query GetPlan($id: ID!) {
    planapusPlan(id: $id) {
      id
      userId
      goal
      summary
      status
      createdAt
      updatedAt
      facts
      parentPlanId
      parentStepId
      steps {
        id
        title
        description
        phaseId
        estimatedDurationDays
        kind
        status
        statusReason
        intent
        parentStepId
        dependsOn
        assignedToId
        assignedRoleId
        expansionPlanId
        producesFact
        consentLevel
        actions {
          id
          name
          description
          mode
          order
          endpoint
          method
          toolRef
          operation
          promptTemplate
          agentProfile
          timeoutMs
        }
        skills {
          id
          name
          intent
          source
          subType
          version
        }
        context {
          notes
          links
          lastUpdatedAt
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        intake {
          intakeRequestId
          slug
          publicUrl
          status
          schema
          normalizedResult
          rawText
          summary
          submittedAt
          fileRefs {
            fileId
            name
            mimeType
            size
            storagePath
          }
        }
        allowedFields {
          key
          label
          type
          required
          description
          enumValues
        }
        assessment {
          id
          isComplete
          completionConfidence
          eligibleForCompletion
          progress {
            structured {
              doneCount
              totalCount
              fraction
              requiredItems {
                key
                label
                status
                evidence
                weight
                category
                condition
                conditionMet
              }
            }
            effortWeighted {
              score
              rationale
            }
          }
          suggestedNext {
            missingInfoQuestions
            recommendedActions
          }
          timestamp
          triggeredBy
          extractedFacts
        }
        inferredRequirements {
          key
          label
          status
          evidence
          weight
          category
          condition
          conditionMet
        }
        applicability {
          applicable
          reason
          dependsOnFacts
          prunedTrigger {
            factKey
            factValue
            description
          }
        }
        requestThreads {
          threadId
          slug
          publicUrl
          status
          createdAt
          result {
            fields
            summary
            completedAt
          }
        }
        dependencyStatus {
          allSatisfied
          blockedByHiddenSteps
          visibleBlockingStepIds
          canStart
          blockedReason
        }
      }
      phases {
        id
        title
        description
        order
        estimatedDurationDays
      }
      participants {
        id
        name
        email
        phone
        inviteStatus
        inviteSentAt
        firebaseUid
      }
      roles {
        roleId
        name
        description
        defaultAssigneePersonId
        required
        isRestricted
        assigneeName
        assigneeEmail
      }
      pendingGenerationJobId
      artifacts {
        id
        planId
        fileName
        mimeType
        sizeBytes
        category
        storagePath
        downloadUrl
        createdAt
      }
    }
  }
`;

export const UPDATE_STEP_STATUS = gql`
  mutation UpdatePlanapusStepStatus($planId: ID!, $stepId: ID!, $status: StepStatus!) {
    updatePlanapusStepStatus(planId: $planId, stepId: $stepId, status: $status) {
      id
      steps {
        id
        status
        context {
          notes
          links
          lastUpdatedAt
          lastUpdatedByUserId
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        statusHistory {
          status
          changedAt
          changedByUserId
          reason
        }
      }
    }
  }
`;

export const UPDATE_STEP_CONTEXT = gql`
  mutation UpdateStepContext($planId: ID!, $stepId: ID!, $context: StepContextInput!) {
    updateStepContext(planId: $planId, stepId: $stepId, context: $context) {
      id
      steps {
        id
        status
        context {
          notes
          links
          lastUpdatedAt
          lastUpdatedByUserId
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        statusHistory {
          status
          changedAt
          changedByUserId
          reason
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

export const TOGGLE_STEP_COMPLETION = gql`
  mutation ToggleStepCompletion($planId: ID!, $stepId: ID!, $completed: Boolean!) {
    toggleStepCompletion(planId: $planId, stepId: $stepId, completed: $completed) {
      id
      steps {
        id
        status
        context {
          notes
          links
          lastUpdatedAt
          lastUpdatedByUserId
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        statusHistory {
          status
          changedAt
          changedByUserId
          reason
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

// ============================================================================
// Conversation Queries & Mutations
// ============================================================================

export const GET_CONVERSATION = gql`
  query GetConversation($planId: ID) {
    getConversation(planId: $planId) {
      id
      userId
      planId
      messages {
        id
        role
        content
        timestamp
      }
      createdAt
      updatedAt
    }
  }
`;

export const SEND_CHAT_MESSAGE = gql`
  mutation SendChatMessage($input: SendChatMessageInput!) {
    sendChatMessage(input: $input) {
      userMessage {
        id
        role
        content
        timestamp
      }
      assistantMessage {
        id
        role
        content
        timestamp
      }
      conversation {
        id
        userId
        planId
        messages {
          id
          role
          content
          timestamp
        }
        createdAt
        updatedAt
      }
      autoAction {
        action
        stepId
        stepTitle
        nextStepId
        nextStepTitle
        message
      }
    }
  }
`;

export const CLEAR_CONVERSATION = gql`
  mutation ClearConversation($planId: ID) {
    clearConversation(planId: $planId)
  }
`;

// ============================================================================
// Participant & Assignment Mutations
// ============================================================================

export const ADD_PARTICIPANT = gql`
  mutation AddParticipant($planId: ID!, $participant: ParticipantInput!) {
    addParticipant(planId: $planId, participant: $participant) {
      id
      participants {
        id
        name
        email
        phone
        inviteStatus
        inviteSentAt
      }
    }
  }
`;

export const ASSIGN_STEP = gql`
  mutation AssignStep($planId: ID!, $stepId: ID!, $participantId: ID) {
    assignStep(planId: $planId, stepId: $stepId, participantId: $participantId) {
      id
      steps {
        id
        assignedToId
      }
      participants {
        id
        name
        email
        phone
        inviteStatus
        inviteSentAt
      }
    }
  }
`;

export const RESEND_PARTICIPANT_INVITE = gql`
  mutation ResendParticipantInvite($planId: ID!, $participantId: ID!) {
    resendParticipantInvite(planId: $planId, participantId: $participantId) {
      id
      participants {
        id
        name
        email
        inviteStatus
        inviteSentAt
      }
    }
  }
`;

export const REMOVE_PARTICIPANT = gql`
  mutation RemoveParticipant($planId: ID!, $participantId: ID!) {
    removeParticipant(planId: $planId, participantId: $participantId) {
      id
      participants {
        id
        name
        email
        phone
        inviteStatus
      }
      steps {
        id
        assignedToId
      }
    }
  }
`;

export const BIND_PARTICIPANT_TO_ROLE = gql`
  mutation BindParticipantToRole($planId: ID!, $roleId: ID!, $personId: ID!) {
    bindParticipantToRole(planId: $planId, roleId: $roleId, personId: $personId) {
      id
      roles {
        roleId
        name
        description
        defaultAssigneePersonId
        required
      }
      participants {
        id
        name
        email
        phone
        inviteStatus
      }
    }
  }
`;

// ============================================================================
// Intake Request Queries & Mutations
// ============================================================================

export const GET_INTAKE_PUBLIC = gql`
  query GetIntakePublic($slug: String!) {
    intakePublic(slug: $slug) {
      id
      title
      description
      schema
      status
      hasFreeFormMode
    }
  }
`;

export const SUBMIT_INTAKE = gql`
  mutation SubmitIntake($slug: String!, $input: IntakeSubmitInput!) {
    submitIntake(slug: $slug, input: $input) {
      status
      normalizedResult
      summary
    }
  }
`;

export const GET_INTAKE_FILE_UPLOAD_URL = gql`
  mutation GetIntakeFileUploadUrl($slug: String!, $fileName: String!, $mimeType: String!) {
    getIntakeFileUploadUrl(slug: $slug, fileName: $fileName, mimeType: $mimeType) {
      fileId
      uploadUrl
      storagePath
    }
  }
`;

export const CREATE_INTAKE_REQUEST = gql`
  mutation CreateIntakeRequest($input: CreateIntakeRequestInput!) {
    createIntakeRequest(input: $input) {
      intakeRequest {
        id
        slug
        publicUrl
        title
        description
        schema
        status
        createdAt
        expiresAt
      }
      publicUrl
    }
  }
`;

// ============================================================================
// Step Intake Mutations
// ============================================================================

export const CREATE_STEP_INTAKE = gql`
  mutation CreateStepIntake($planId: ID!, $stepId: ID!, $prompt: String, $schema: JSON) {
    createStepIntake(planId: $planId, stepId: $stepId, prompt: $prompt, schema: $schema) {
      id
      steps {
        id
        status
        intake {
          intakeRequestId
          slug
          publicUrl
          status
          schema
          normalizedResult
          rawText
          summary
          submittedAt
        }
      }
    }
  }
`;

export const SUBMIT_STEP_INTAKE_INLINE = gql`
  mutation SubmitStepIntakeInline($planId: ID!, $stepId: ID!, $input: IntakeSubmitInput!) {
    submitStepIntakeInline(planId: $planId, stepId: $stepId, input: $input) {
      id
      steps {
        id
        status
        intake {
          intakeRequestId
          slug
          publicUrl
          status
          schema
          normalizedResult
          rawText
          summary
          submittedAt
          fileRefs {
            fileId
            name
            mimeType
            size
            storagePath
          }
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

export const RETRY_CONNECTOR_STEP = gql`
  mutation RetryConnectorStep($planId: ID!, $stepId: ID!) {
    retryConnectorStep(planId: $planId, stepId: $stepId) {
      id
      steps {
        id
        status
        context {
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

export const REMOVE_STEP_INTAKE = gql`
  mutation RemoveStepIntake($planId: ID!, $stepId: ID!) {
    removeStepIntake(planId: $planId, stepId: $stepId) {
      id
      steps {
        id
        intake {
          intakeRequestId
        }
      }
    }
  }
`;

// ============================================================================
// Pre-Plan Analysis Query
// ============================================================================

/**
 * Analyze a goal before generating a plan.
 * Returns research findings and clarifying questions.
 */
export const ANALYZE_PLAN_GOAL = gql`
  query AnalyzePlanGoal($input: AnalyzePlanGoalInput!) {
    analyzePlanGoal(input: $input) {
      originalPrompt
      goalInterpretation {
        whatUserWants
        measurableOutcome
        implicitConstraints
      }
      domainResearch {
        findings {
          topic
          summary
          details
          source
          confidence
        }
        searchQueries
        sources
        generatedAt
      }
      clarifyingQuestions {
        id
        question
        why
        type
        choices
        required
        defaultValue
      }
      readyToGenerate
      errors
    }
  }
`;

// ============================================================================
// Debug / Development Mutations
// ============================================================================

export const RESET_STEP = gql`
  mutation ResetStep($planId: ID!, $stepId: ID!) {
    resetStep(planId: $planId, stepId: $stepId) {
      id
      steps {
        id
        status
        context {
          notes
          links
          lastUpdatedAt
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        statusHistory {
          status
          changedAt
          changedByUserId
          reason
        }
        intake {
          intakeRequestId
          status
        }
        assessment {
          id
          isComplete
          completionConfidence
        }
        inferredRequirements {
          key
          label
          status
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

export const RESET_PLAN = gql`
  mutation ResetPlan($planId: ID!) {
    resetPlan(planId: $planId) {
      id
      facts
      steps {
        id
        status
        context {
          notes
          links
          lastUpdatedAt
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        statusHistory {
          status
          changedAt
          changedByUserId
          reason
        }
        intake {
          intakeRequestId
          status
        }
        assessment {
          id
          isComplete
          completionConfidence
        }
        inferredRequirements {
          key
          label
          status
        }
        applicability {
          applicable
          reason
          dependsOnFacts
          prunedTrigger {
            factKey
            factValue
            description
          }
        }
      }
      evaluation {
        evaluatedAt
        totalSteps
        completedSteps
        inProgressSteps
        blockedSteps
        percentComplete
      }
    }
  }
`;

// ============================================================================
// Request Thread Queries & Mutations
// ============================================================================

/**
 * Get public thread data by slug (no auth required for PUBLIC threads).
 */
export const GET_REQUEST_THREAD_PUBLIC = gql`
  query GetRequestThreadPublic($slug: String!) {
    requestThreadPublic(slug: $slug) {
      id
      parentStepTitle
      requestSpec {
        ask
        description
        allowedFields {
          key
          label
          type
          required
          description
          enumValues
        }
        outputContract {
          allowedKeys
        }
        uiMode
      }
      accessMode
      status
      messages {
        id
        role
        content
        timestamp
        senderId
      }
      uploads {
        id
        fileName
        mimeType
        size
        uploadedAt
      }
      satisfactionState {
        isReady
        confidence
        extractedFields
        missingFields
        progressSummary
        evaluatedAt
      }
    }
  }
`;

/**
 * Post a message to a thread.
 */
export const POST_THREAD_MESSAGE = gql`
  mutation PostThreadMessage($slug: String!, $input: PostThreadMessageInput!) {
    postThreadMessage(slug: $slug, input: $input) {
      requestThread {
        id
        status
        messages {
          id
          role
          content
          timestamp
          senderId
        }
      }
      userMessage {
        id
        role
        content
        timestamp
      }
      assistantMessage {
        id
        role
        content
        timestamp
      }
      satisfactionState {
        isReady
        confidence
        extractedFields
        missingFields
        progressSummary
        evaluatedAt
      }
    }
  }
`;

/**
 * Update thread fields directly via form (without LLM).
 * Used for form-first mode where users fill structured fields.
 */
export const UPDATE_THREAD_FIELDS = gql`
  mutation UpdateThreadFields($slug: String!, $input: UpdateThreadFieldsInput!) {
    updateThreadFields(slug: $slug, input: $input) {
      requestThread {
        id
        status
      }
      satisfactionState {
        isReady
        confidence
        extractedFields
        missingFields
        progressSummary
        evaluatedAt
      }
      fieldErrors
    }
  }
`;

/**
 * Confirm thread satisfaction and apply result to parent.
 */
export const CONFIRM_THREAD_SATISFACTION = gql`
  mutation ConfirmThreadSatisfaction($slug: String!) {
    confirmThreadSatisfaction(slug: $slug) {
      requestThread {
        id
        status
        result {
          fields
          summary
          confidence
          confirmedAt
        }
      }
      result {
        fields
        summary
        confidence
        confirmedAt
      }
      parentStepUpdated
    }
  }
`;

/**
 * Create a new request thread for a step.
 */
export const CREATE_REQUEST_THREAD = gql`
  mutation CreateRequestThread($input: CreateRequestThreadInput!) {
    createRequestThread(input: $input) {
      requestThread {
        id
        slug
        publicUrl
        parentStepTitle
        status
        accessMode
        requestSpec {
          ask
          description
          allowedFields {
            key
            label
            type
            required
          }
        }
        messages {
          id
          role
          content
          timestamp
        }
      }
      publicUrl
    }
  }
`;

/**
 * Delete a request thread from a step.
 */
export const DELETE_STEP_REQUEST_THREAD = gql`
  mutation DeleteStepRequestThread($planId: ID!, $stepId: ID!, $threadId: ID!) {
    deleteStepRequestThread(planId: $planId, stepId: $stepId, threadId: $threadId) {
      id
      steps {
        id
        requestThreads {
          threadId
          slug
          publicUrl
          status
          createdAt
          result {
            fields
            summary
            completedAt
          }
        }
      }
    }
  }
`;

// ============================================================================
// Description Extraction
// ============================================================================

/**
 * Extract checklist items from a step description using AI.
 * Results should be cached client-side by stepId.
 */
export const EXTRACT_DESCRIPTION_ITEMS = gql`
  query ExtractDescriptionItems($description: String!) {
    extractDescriptionItems(description: $description) {
      hasItems
      items
    }
  }
`;

// ============================================================================
// Projected Plan View (Visibility-Filtered)
// ============================================================================

/**
 * Get projected plan view based on viewer's access level.
 * Owner/ADMIN: full plan visibility
 * ROLE_MEMBER: filtered view with only assigned steps, plus context fillers
 */
export const GET_PROJECTED_PLAN = gql`
  query GetProjectedPlan($planId: ID!) {
    projectedPlan(planId: $planId) {
      id
      goal
      summary
      status
      createdAt
      phases {
        id
        title
        description
        order
        estimatedDurationDays
      }
      visibleSteps {
        id
        title
        description
        phaseId
        estimatedDurationDays
        kind
        status
        statusReason
        intent
        parentStepId
        dependsOn
        assignedToId
        assignedRoleId
        expansionPlanId
        producesFact
        consentLevel
        allowedFields {
          key
          label
          type
          required
          description
          enumValues
        }
        actions {
          id
          name
          description
          mode
          order
          endpoint
          method
          toolRef
          operation
          promptTemplate
          agentProfile
          timeoutMs
        }
        skills {
          id
          name
          intent
          source
          subType
          version
        }
        context {
          notes
          links
          lastUpdatedAt
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        intake {
          intakeRequestId
          slug
          publicUrl
          status
          schema
          normalizedResult
          rawText
          summary
          submittedAt
          fileRefs {
            fileId
            name
            mimeType
            size
            storagePath
          }
        }
        assessment {
          id
          isComplete
          completionConfidence
          eligibleForCompletion
          progress {
            structured {
              doneCount
              totalCount
              fraction
              requiredItems {
                key
                label
                status
                evidence
                weight
                category
                condition
                conditionMet
              }
            }
            effortWeighted {
              score
              rationale
            }
          }
          suggestedNext {
            missingInfoQuestions
            recommendedActions
          }
          timestamp
          triggeredBy
          extractedFacts
        }
        inferredRequirements {
          key
          label
          status
          evidence
          weight
          category
          condition
          conditionMet
        }
        applicability {
          applicable
          reason
          dependsOnFacts
          prunedTrigger {
            factKey
            factValue
            description
          }
        }
        requestThreads {
          threadId
          slug
          publicUrl
          status
          createdAt
          result {
            fields
            summary
            completedAt
          }
        }
      }
      hiddenStepFillers {
        statusBucket
        blockedReasonCategory
        titleRedacted
        phaseId
      }
      phaseSummaries {
        phaseId
        phaseLabel
        countByStatusBucket {
          pending
          active
          blocked
          done
        }
        totalSteps
        visibleSteps
        hiddenSteps
      }
      viewerPersonId
      viewerAccessLevel
      viewerRoleIds
      isOwner
    }
  }
`;

// ============================================================================
// Participant Workspace Query
// ============================================================================

/**
 * Get participant workspace view for ROLE_MEMBER users.
 * Returns a focused, single-screen experience for task execution.
 * Only available to ROLE_MEMBER participants (not owners or ADMINs).
 */
export const GET_PARTICIPANT_WORKSPACE = gql`
  query GetParticipantWorkspace($planId: ID!) {
    participantWorkspace(planId: $planId) {
      planId
      planTitle
      planSummary
      planStatus
      updatedAt
      mySteps {
        stepId
        title
        description
        status
        phaseLabel
        blocked {
          isBlocked
          hiddenPrereqCount
          visiblePrereqIds
          blockedReason
          canStart
        }
        context {
          notes
          links
          lastUpdatedAt
          collectedData
          connectorWait
          connectorPollResult
          connectorActivityLog
          connectorResponse
          lastConnectorSubmitInput
        }
        updatedAt
        assignedViaRole
      }
      fuzzyContext {
        totals {
          todo
          inProgress
          blocked
          completed
        }
        phases {
          phaseId
          phaseLabel
          counts {
            todo
            inProgress
            blocked
            completed
          }
        }
        totalSteps
        visibleSteps
      }
      viewerPersonId
      viewerRoleIds
    }
  }
`;

// ============================================================================
// Guide Landing Page + Journey
// ============================================================================

export const GET_GUIDE_LANDING = gql`
  query GetGuideLanding($orgSlug: String!, $guideSlug: String!) {
    guideLanding(orgSlug: $orgSlug, guideSlug: $guideSlug) {
      id
      orgSlug
      slug
      title
      description
      publishedAt
      hero
      starterRoleId
      generationSourceId
      deadlines {
        label
        date
        description
      }
      faqBlocks {
        question
        answer
      }
      steps {
        id
        guideId
        order
        title
        summary
      }
      templatePlanId
      theme {
        brandProfileId
        brandMode
        version
        accessibilityAdjusted
        resolvedFromOrgId
        overrides {
          organizationDisplayName
          logoUrl
          headerLogoUrl
          primaryColor
          secondaryColor
          tone
        }
      }
      resolvedTheme {
        organizationDisplayName
        logoUrl
        headerLogoUrl
        primaryColor
        secondaryColor
        backgroundColor
        textColor
        ctaTextColor
        tone
        source
        profileId
        confidenceScore
        accessibilityAdjusted
        warnings
      }
    }
  }
`;

export const START_JOURNEY = gql`
  mutation StartJourney(
    $orgSlug: String!
    $guideSlug: String!
    $applicantEmail: String!
    $starterRoleId: ID
    $additionalRoleIds: [ID!]
  ) {
    startJourney(
      orgSlug: $orgSlug
      guideSlug: $guideSlug
      applicantEmail: $applicantEmail
      starterRoleId: $starterRoleId
      additionalRoleIds: $additionalRoleIds
    ) {
      planId
      message
    }
  }
`;

export const LIST_GUIDES = gql`
  query ListGuides {
    listGuides {
      id
      orgSlug
      slug
      title
      description
      publishedAt
      steps {
        id
        order
        title
      }
    }
  }
`;

export const GET_GUIDE_STARTER_INFO = gql`
  query GetGuideStarterInfo($orgSlug: String!, $guideSlug: String!) {
    guideStarterInfo(orgSlug: $orgSlug, guideSlug: $guideSlug) {
      guideId
      title
      needsRoleSelection
      availableRoles {
        roleId
        name
        description
      }
      preSelectedRoleId
    }
  }
`;

export const GET_FEATURED_GUIDES = gql`
  query GetFeaturedGuides {
    featuredGuides {
      id
      orgSlug
      slug
      title
      shortDescription
      publisherOrgName
      isOfficial
    }
  }
`;

// ============================================================================
// Guide Edit Data (Versioning)
// ============================================================================

export const GET_GUIDE_EDIT_DATA = gql`
  query GetGuideEditData($guideId: ID!) {
    guideEditData(guideId: $guideId) {
      id
      orgId
      orgSlug
      slug
      title
      sourceGroupId
      generatedAt
      isOutOfDate
      theme {
        brandProfileId
        brandMode
        version
        accessibilityAdjusted
        resolvedFromOrgId
        overrides {
          organizationDisplayName
          logoUrl
          headerLogoUrl
          primaryColor
          secondaryColor
          tone
        }
      }
      resolvedTheme {
        organizationDisplayName
        logoUrl
        headerLogoUrl
        primaryColor
        secondaryColor
        backgroundColor
        textColor
        ctaTextColor
        tone
        source
        profileId
        confidenceScore
        accessibilityAdjusted
        warnings
      }
      publishedVersion {
        id
        guideId
        versionNumber
        status
        generationPrompt
        generationFileIds
        generationFiles { id fileName mimeType sizeBytes }
        generationSourceId
        generatedOutput {
          title
          description
          hero
          deadlines { label date description }
          faqBlocks { question answer }
          steps { id guideId order title summary }
          templatePlanId
          applicantRoleId
          starterRoleId
        }
        generationStatus
        generationError
        createdByUserId
        createdAt
        publishedAt
      }
      draftVersion {
        id
        guideId
        versionNumber
        status
        generationPrompt
        generationFileIds
        generationFiles { id fileName mimeType sizeBytes }
        generationSourceId
        generatedOutput {
          title
          description
          hero
          deadlines { label date description }
          faqBlocks { question answer }
          steps { id guideId order title summary }
          templatePlanId
          applicantRoleId
          starterRoleId
        }
        generationStatus
        generationError
        createdByUserId
        createdAt
        publishedAt
      }
    }
  }
`;

// ============================================================================
// Organization Queries
// ============================================================================

export const MY_ORGANIZATIONS = gql`
  query MyOrganizations {
    myOrganizations {
      id
      name
      slug
      website
      createdAt
    }
  }
`;

export const ACTIVE_ORGANIZATION = gql`
  query ActiveOrganization {
    activeOrganization {
      organization {
        id
        name
        slug
        website
        createdAt
      }
      role
    }
  }
`;

export const CHECK_SLUG_AVAILABILITY = gql`
  query CheckSlugAvailability($slug: String!) {
    checkSlugAvailability(slug: $slug) {
      slug
      available
      error
    }
  }
`;

export const GET_ORGANIZATION_BRAND_PROFILES = gql`
  query GetOrganizationBrandProfiles(
    $orgId: ID
    $status: BrandProfileStatus
    $includeArchived: Boolean
  ) {
    organizationBrandProfiles(
      orgId: $orgId
      status: $status
      includeArchived: $includeArchived
    ) {
      id
      orgId
      name
      status
      isDefault
      confidenceScore
      organizationDisplayName
      logoUrl
      headerLogoUrl
      palette {
        primary
        secondary
        background
        surface
        text
      }
      tone
      createdFromImportId
      importedAssetRefs
      provenance
      approvedBy
      createdAt
      updatedAt
    }
  }
`;

export const GET_ORGANIZATION_BRAND_PROFILE_IMPACT = gql`
  query GetOrganizationBrandProfileImpact($profileId: ID!) {
    organizationBrandProfileImpact(profileId: $profileId) {
      profileId
      orgId
      orgDefaultGuideCount
      pinnedImportProfileGuideCount
      migratableGuideIds
    }
  }
`;

// ============================================================================
// Job Queries
// ============================================================================

export const GET_JOB = gql`
  query GetJob($id: ID!) {
    job(id: $id) {
      id
      orgId
      type
      status
      currentStage
      inputSummary
      inputSnapshot
      resultRef
      resultData
      errorMessage
      createdAt
      startedAt
      finishedAt
    }
  }
`;

export const GET_JOB_EVENTS = gql`
  query GetJobEvents($jobId: ID!, $limit: Int) {
    jobEvents(jobId: $jobId, limit: $limit) {
      timestamp
      stage
      message
      metadata
    }
    jobEventCount(jobId: $jobId)
  }
`;

export const ORG_JOBS = gql`
  query OrgJobs($orgId: ID!, $status: [JobStatus!], $limit: Int) {
    orgJobs(orgId: $orgId, status: $status, limit: $limit) {
      id
      type
      status
      currentStage
      inputSummary
      resultRef
      errorMessage
      createdAt
      startedAt
      finishedAt
    }
  }
`;

// ============================================================================
// Group Suggestions
// ============================================================================

export const GET_GROUP_SUGGESTIONS = gql`
  query GetGroupSuggestions($orgId: ID!) {
    groupSuggestions(orgId: $orgId) {
      id
      name
      description
      rationale
      score
      status
      scoreBreakdown {
        goalClarity
        personaFit
        urlNeighborhood
        contentSimilarity
        sharedEntities
      }
      sourceItems {
        id
        title
        type
        mimeType
      }
      acceptedGroupId
      createdAt
    }
  }
`;

// Legacy alias (used by ProcessLandingPage)
export const GET_PROCESS_LANDING = GET_GUIDE_LANDING;

export const VALIDATE_JOURNEY_MAGIC_LINK = gql`
  query ValidateJourneyMagicLink($token: String!) {
    validateJourneyMagicLink(token: $token) {
      valid
      planId
      processTitle
      applicantEmail
      error
      roles {
        roleId
        name
        description
        assigneeName
        assigneeEmail
      }
    }
  }
`;

export const CONSUME_JOURNEY_MAGIC_LINK = gql`
  mutation ConsumeJourneyMagicLink($token: String!) {
    consumeJourneyMagicLink(token: $token) {
      valid
      firebaseToken
      planId
      processTitle
      applicantEmail
      error
    }
  }
`;

/**
 * Get a public plan by ID (no auth required).
 * Used by the homepage guide preview drawer to fetch a guide's backing plan.
 */
export const GET_PUBLIC_PLAN = gql`
  query GetPublicPlan($planId: ID!) {
    getPublicPlan(planId: $planId) {
      id
      goal
      summary
      status
      phases {
        id
        title
        description
        order
        estimatedDurationDays
      }
      steps {
        id
        title
        description
        phaseId
        kind
        status
        statusReason
        intent
        dependsOn
        assignedRoleId
        estimatedDurationDays
        parentStepId
      }
      roles {
        roleId
        name
        description
        required
      }
    }
  }
`;

// ============================================================================
// Document Groups
// ============================================================================

export const GET_DOCUMENT_GROUPS = gql`
  query GetDocumentGroups($orgId: ID!) {
    documentGroups(orgId: $orgId) {
      id
      orgId
      name
      description
      groupType
      version
      versionHash
      createdAt
      updatedAt
      members {
        id
        sourceItemId
      }
      linkedGuide {
        id
        title
        publishedAt
        sourceGroupVersionHash
        generatedAt
        isOutOfDate
      }
    }
  }
`;

export const GET_DOCUMENT_GROUP = gql`
  query GetDocumentGroup($id: ID!) {
    documentGroup(id: $id) {
      id
      orgId
      name
      description
      groupType
      version
      versionHash
      createdAt
      updatedAt
      members {
        id
        groupId
        sourceItemId
        assignedBy
        createdAt
        sourceItem {
          id
          orgId
          title
          type
          mimeType
          sourceUrl
          sizeBytes
          extractionStatus
          reviewStatus
          primaryOwnerGroupId
          createdAt
          updatedAt
        }
      }
      linkedGuide {
        id
        title
        slug
        orgSlug
        publishedAt
        sourceGroupVersionHash
        generatedAt
        isOutOfDate
        needsOwnerResolution
        steps {
          id
          order
          title
          summary
        }
      }
    }
  }
`;

// ============================================================================
// Source Library
// ============================================================================

export const GET_SOURCE_ITEMS = gql`
  query GetSourceItems($orgId: ID!) {
    sourceItems(orgId: $orgId) {
      id
      orgId
      title
      type
      mimeType
      sizeBytes
      extractionStatus
      classificationStatus
      classificationWave1 {
        is_process
        references_process
        confidence
        doc_type_hint
      }
      classificationOverride {
        isProcess
        setBy
        setAt
      }
      reviewStatus
      linkedFromPages {
        url
        title
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_SOURCE_ITEM = gql`
  query GetSourceItem($id: ID!) {
    sourceItem(id: $id) {
      id
      title
      type
      mimeType
      sourceUrl
      sizeBytes
      extractionStatus
      classificationStatus
      reviewStatus
      extractedText
      extractionMetadata {
        summary
        topics
        documentType
        audience
        entities
      }
      classificationWave1 {
        is_process
        references_process
        confidence
        signals
        doc_type_hint
        needs_fallback
        wave
        classifiedAt
      }
      classificationWave2 {
        is_process
        references_process
        confidence
        signals
        doc_type_hint
        needs_fallback
        wave
        classifiedAt
      }
      classificationOverride {
        isProcess
        setBy
        setAt
      }
      linkedFromPages {
        url
        title
      }
      summary
      summarizationStatus
      analysis {
        summary
        topic
        audience
        guideable
        guideable_rationale
        guide_title
        flags
        wave
        model
        analyzedAt
      }
      analysisStatus
      storagePath
      createdAt
      updatedAt
    }
  }
`;

export const GET_SOURCE_ITEM_TREE = gql`
  query GetSourceItemTree($orgId: ID!, $parentId: String, $hideEmpty: Boolean) {
    sourceItemTree(orgId: $orgId, parentId: $parentId, hideEmpty: $hideEmpty) {
      id
      kind
      label
      url
      title
      count
      hasChildren
    }
  }
`;

export const GET_SOURCE_ITEMS_CONNECTION = gql`
  query GetSourceItemsConnection($orgId: ID!, $filter: SourceItemFilter, $first: Int, $after: String) {
    sourceItemsConnection(orgId: $orgId, filter: $filter, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          orgId
          title
          type
          mimeType
          sizeBytes
          extractionStatus
          classificationStatus
          classificationWave1 {
            is_process
            references_process
            confidence
            doc_type_hint
          }
          classificationOverride {
            isProcess
            setBy
            setAt
          }
          reviewStatus
          linkedFromPages {
            url
            title
          }
          summary
          summarizationStatus
          analysis {
            summary
            topic
            audience
            guideable
            guideable_rationale
            guide_title
            flags
            wave
          }
          analysisStatus
          createdAt
          updatedAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_SOURCE_ITEMS_EXPORT = gql`
  query GetSourceItemsExport($orgId: ID!, $filter: SourceItemFilter, $first: Int, $after: String) {
    sourceItemsConnection(orgId: $orgId, filter: $filter, first: $first, after: $after) {
      edges {
        cursor
        node {
          id
          title
          type
          sourceUrl
          extractionStatus
          extractionMetadata {
            summary
            topics
            documentType
            audience
            entities
          }
          classificationStatus
          classificationWave1 {
            is_process
            references_process
            confidence
            doc_type_hint
          }
          classificationWave2 {
            is_process
            references_process
            confidence
            doc_type_hint
          }
          classificationOverride {
            isProcess
          }
          linkedFromPages {
            url
            title
          }
          summary
          summarizationStatus
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_SOURCE_ITEM_DEPENDENCIES = gql`
  query GetSourceItemDependencies($sourceItemId: ID!) {
    sourceItemDependencies(sourceItemId: $sourceItemId) {
      groupMemberships { groupId groupName }
      citations { guideId stepId }
      suggestions { suggestionId suggestionName }
      canDelete
    }
  }
`;

export const GET_GUIDE_CITATIONS = gql`
  query GetGuideCitations($guideId: ID!, $stepId: ID) {
    guideCitations(guideId: $guideId, stepId: $stepId) {
      id
      guideId
      guideVersionId
      stepId
      sourceItemId
      sourceLocator {
        page
        url
        selector
        paragraphIndex
        heading
      }
      excerpt
      createdAt
      sourceItem {
        id
        title
        type
        mimeType
      }
    }
  }
`;

// ============================================================================
// Web Crawl Queries
// ============================================================================

export const ORG_WEB_CRAWLS = gql`
  query OrgWebCrawls($orgId: ID!) {
    orgWebCrawls(orgId: $orgId) {
      id
      seedUrl
      status
      maxDepth
      maxPages
      pagesDiscovered
      pagesCrawled
      documentsFound
      documentsImported
      generationJobId
      createdAt
      updatedAt
      finishedAt
    }
  }
`;

export const GET_WEB_CRAWL = gql`
  query GetWebCrawl($id: ID!) {
    webCrawl(id: $id) {
      id
      orgId
      seedUrl
      status
      maxDepth
      maxPages
      allowedDomains
      pagesDiscovered
      pagesCrawled
      documentsFound
      documentsImported
      generationJobId
      createdAt
      updatedAt
      finishedAt
      brandExtraction {
        status
        confidence
        warnings
        sourceArtifacts
        discoveredAt
        brandCandidates {
          tokenType
          value
          sourceRef
          confidence
          rationale
          dedupeHash
        }
        selectedCandidates {
          logoUrl
          headerLogoUrl
          primaryColor
          secondaryColor
          organizationDisplayName
          tone
        }
      }
    }
  }
`;

export const GET_WEB_CRAWL_PAGES = gql`
  query GetWebCrawlPages($crawlId: ID!) {
    webCrawlPages(crawlId: $crawlId) {
      id
      url
      title
      depth
      links
      documents {
        url
        filename
        mimeType
        sizeBytes
        linkedFromPages {
          url
          title
        }
        sourceItemId
        downloadStatus
      }
      contentPreview
      crawledAt
      statusCode
      error
    }
  }
`;

// ============================================================================
// Eval Queries
// ============================================================================

export const GET_EVAL_TEST_CASES = gql`
  query GetEvalTestCases {
    evalTestCases {
      id
      sourceItemId
      sourceItemTitle
      addedAt
      addedBy
      itemType
      expected {
        guideable
        topic
        audience
      }
    }
  }
`;

export const GET_EVAL_REPORTS = gql`
  query GetEvalReports {
    evalReports {
      id
      promptName
      promptExists
      modelName
      ranAt
      totalCases
      passed
      failed
      inputTokens
      outputTokens
      totalCost
      weightedScore
    }
  }
`;

export const GET_OPENROUTER_MODELS = gql`
  query GetOpenRouterModels($ids: [String!]) {
    openRouterModels(ids: $ids) {
      id
      name
      inputPer1M
      outputPer1M
      contextLength
    }
  }
`;

export const GET_EVAL_REPORT = gql`
  query GetEvalReport($id: String!) {
    evalReport(id: $id) {
      id
      promptName
      promptExists
      ranAt
      totalCases
      passed
      failed
      baselineReportId
      modelName
      guideableScore
      overallScore
      weightedScore
      fieldAccuracy {
        guideable { correct total }
        topic { correct total }
        audience { correct total }
      }
      results {
        testCaseId
        sourceItemTitle
        pass
        expected { guideable topic audience }
        actual { guideable topic audience }
        diffs
      }
      inputTokens
      outputTokens
      totalCost
    }
  }
`;

export const GET_EVAL_PROMPTS = gql`
  query GetEvalPrompts {
    evalPrompts {
      name
      prompt
      createdAt
      displayName
      origin
      parentName
      lastEvalScore {
        passed
        total
        accuracy
        guideableAccuracy
        weightedScore
        reportId
        ranAt
      }
    }
  }
`;

export const GET_EVAL_PROMPT = gql`
  query GetEvalPrompt($name: String!) {
    evalPrompt(name: $name) {
      name
      prompt
      createdAt
      displayName
      origin
      parentName
      lastEvalScore {
        passed
        total
        accuracy
        guideableAccuracy
        weightedScore
        reportId
        ranAt
      }
    }
  }
`;

// ============================================================================
// Integration Connections
// ============================================================================

export const GET_MY_CONNECTIONS = gql`
  query GetMyConnections {
    myConnections {
      id
      tier
      integrationId
      provider
      status
      orgId
      principalId
      runId
      createdAt
      updatedAt
      lastUsedAt
      lastUsedByRunId
      usedByOrgIds
      expiresAt
    }
  }
`;

export const GET_ORG_CONNECTIONS = gql`
  query GetOrgConnections($orgId: ID!) {
    orgConnections(orgId: $orgId) {
      id
      tier
      integrationId
      provider
      status
      orgId
      principalId
      createdAt
      updatedAt
      lastUsedAt
      lastUsedByRunId
    }
  }
`;

/**
 * S8 — admin audit feed for org integrations. Backs the "Audit"
 * panel on the Org Integrations page. Returns metadata-only rows
 * for: (a) every ORG-tier connection the org owns, plus (b) every
 * USER-tier connection that consented to ORG→USER fallback inside
 * one of the org's runs. Tokens, composioConnectedAccountId, and per-row
 * error strings are intentionally absent (M2 trust boundary,
 * ADR-0002). Sorted recent-use first by the resolver.
 */
export const GET_ORG_CONNECTION_AUDIT = gql`
  query GetOrgConnectionAudit($orgId: ID!) {
    orgConnectionAudit(orgId: $orgId) {
      integrationId
      principalId
      tier
      createdAt
      lastUsedAt
      lastUsedByRunId
    }
  }
`;

/**
 * S6 — Integration manifest projection used by the Org Integrations
 * page (tier: ORG), the personal Connections page (tier: USER), and
 * the JIT Connect picker (tier: RUN). Token material is never on
 * this query — see `IntegrationCatalogEntry` in `planapus.graphql`.
 */
export const GET_INTEGRATION_CATALOG = gql`
  query GetIntegrationCatalog($tier: ConnectionTier) {
    integrationCatalog(tier: $tier) {
      id
      provider
      name
      category
      description
      priority
      authType
      allowedTiers
      defaultTier
      capabilities
      industries
    }
  }
`;

/**
 * S4 — JIT Connect requirement lookup. The `ConnectionRequiredContent`
 * step mode fires this when the step UI mounts in
 * `BLOCKED_CONNECTION_REQUIRED` mode so it can render the consumer
 * pitch + tier picker without bloating GET_PLAN.
 *
 * Returns null when the step already has an active resolved
 * connection — caller should treat null as "step is no longer
 * blocked" and refetch the plan.
 */
export const GET_CONNECTION_REQUIREMENT = gql`
  query GetConnectionRequirement($runId: ID!, $stepId: String!) {
    connectionRequirement(runId: $runId, stepId: $stepId) {
      integrationId
      allowedTiers
      defaultTier
      reason
      consumerPitch
    }
  }
`;

/**
 * S12 — Platform Admin dashboard view. Gated server-side via
 * `requirePlatformAdmin` (Firebase custom claim `platformAdmin: true`
 * OR allow-list env var). The frontend does not gate by claim — it
 * just renders the page and lets the server reject non-admins; the
 * route itself is intentionally unlinked from the main nav so only
 * folks with the URL discover it.
 */
export const GET_PLATFORM_INTEGRATIONS = gql`
  query GetPlatformIntegrations {
    platformIntegrations {
      integrationId
      health
      lastHealthCheckAt
      rollingCallCount24h
      rollingErrorRate24h
      healthMessage
      latencyMs
      composioToolkitSlug
    }
  }
`;

// ============================================================================
// Notifications
// ============================================================================

export const GET_MY_NOTIFICATION_PREFERENCES = gql`
  query GetMyNotificationPreferences {
    myNotificationPreferences {
      channels
      quietHoursStart
      quietHoursEnd
      digestFrequency
      updatedAt
    }
  }
`;

export const GET_MY_NOTIFICATIONS = gql`
  query GetMyNotifications($limit: Int) {
    myNotifications(limit: $limit) {
      id
      userId
      title
      body
      type
      read
      data
      createdAt
    }
  }
`;

export const GET_MY_UNREAD_NOTIFICATION_COUNT = gql`
  query GetMyUnreadNotificationCount {
    myUnreadNotificationCount
  }
`;

// ============================================================================
// Person Profiles
// ============================================================================

export const GET_PERSON_PROFILE = gql`
  query GetPersonProfile($email: String!) {
    personProfile(email: $email) {
      email
      name
      phone
      company
      title
      location
      linkedInUrl
      avatarUrl
      tags
      sources
      enrichedAt
    }
  }
`;

export const SEARCH_PERSON_PROFILES = gql`
  query SearchPersonProfiles($query: String!, $limit: Int) {
    searchPersonProfiles(query: $query, limit: $limit) {
      email
      name
      company
      title
      avatarUrl
      tags
    }
  }
`;

// ============================================================================
// Google Drive
// ============================================================================

export const GET_DRIVE_FILES = gql`
  query GetDriveFiles($folderId: String, $query: String, $limit: Int) {
    driveFiles(folderId: $folderId, query: $query, limit: $limit) {
      id
      name
      mimeType
      webViewLink
      iconLink
      modifiedTime
      size
    }
  }
`;
